import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Typography
} from '@mui/material'
import DropzoneS3Uploader from 'react-dropzone-s3-uploader'
import axios from 'axios'
import {
  createCancellationRequest,
  crudApiInstance,
  getS3BucketSignedUrlForPolicyDcpDocuments,
  uploadPolicyDocument
} from '../../../../../lib/requests.es6'
import {
  getPartnerGuid,
  hasAuthourity,
  getAuthorizationToken
} from '../../../../../lib/access.es6'

const S3_URL = 'https://policy-onboarding-documents.s3.amazonaws.com'

const CancellationRequestDialog = ({
  open,
  onClose,
  policy,
  formData,
  onFormDataChange
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [progress, setProgress] = useState(0)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [additionalNotes, setAdditionalNotes] = useState('')

  const partner = getPartnerGuid()
  const hasRequestRole = hasAuthourity('OMM_POLICY_CANCELLATION_REQUEST')

  if (open && !hasRequestRole) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Request Policy Cancellation</DialogTitle>
        <DialogContent>
          <Alert severity="error">
            You do not have permission to create cancellation requests. Required
            role: OMM_POLICY_CANCELLATION_REQUEST
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    )
  }

  const uploadCancellationSupportingDocument = async (
    originalFilename,
    url
  ) => {
    return uploadPolicyDocument({
      policy_guid: policy.guid,
      description: 'Cancellation Supporting Document',
      filename: originalFilename,
      url: url
    })
  }

  const handleDocumentUploadFinished = async info => {
    try {
      const originalFilename = info.filename || (info.file && info.file.name)
      const url = info.fileUrl.replace('undefined', info.bucketKey)

      await uploadCancellationSupportingDocument(originalFilename, url)
      setUploadedFile({
        filename: originalFilename,
        url: url
      })
      setError(null)
    } catch (err) {
      const errorMsg =
        err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : 'Failed to upload document'
      setError(errorMsg)
    }
  }

  const getS3SignedUrl = (file, callback) => {
    getS3BucketSignedUrlForPolicyDcpDocuments(policy.guid, file, callback)
  }

  const uploadOptions = {
    getSignedUrl: getS3SignedUrl,
    s3Url: S3_URL,
    uploadRequestHeaders: {},
    onProgress: progress => setProgress(progress),
    onError: errorMessage => setError(errorMessage)
  }

  const handleSubmit = async () => {
    try {
      setLoading(true)
      setError(null)

      if (!uploadedFile) {
        setError('Please upload a supporting document')
        setLoading(false)
        return
      }

      const selectedDate = new Date(formData.effective_date)
      const today = new Date()
      selectedDate.setHours(0, 0, 0, 0)
      today.setHours(0, 0, 0, 0)

      let effective_date_type
      if (selectedDate.getTime() < today.getTime()) {
        effective_date_type = 'Backdated'
      } else if (selectedDate.getTime() === today.getTime()) {
        effective_date_type = 'Today'
      } else {
        effective_date_type = 'Future'
      }

      const payload = {
        partner,
        policy_guid: policy.guid,
        reason: formData.reason,
        effective_date_type,
        effective_date: new Date(formData.effective_date)
          .toISOString()
          .split('T')[0],
        additional_details: additionalNotes ? { notes: additionalNotes } : null
      }

      const response = await createCancellationRequest(payload)

      setSuccess(true)
      setTimeout(() => {
        onClose()
        setSuccess(false)
      }, 2000)
    } catch (err) {
      const errorMsg =
        err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : 'Failed to create cancellation request'
      setError(errorMsg)
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request Policy Cancellation</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {success && (
            <Alert severity="success">
              Cancellation request created successfully. Redirecting...
            </Alert>
          )}

          {/* Effective Date (Required) */}
          <TextField
            type="date"
            label="Effective Date"
            fullWidth
            value={formData.effective_date}
            onChange={e =>
              onFormDataChange('effective_date', e.target.value)
            }
            InputLabelProps={{ shrink: true }}
            disabled={loading || success}
            required
          />

          {/* Reason (Required) */}
          <TextField
            label="Reason for Cancellation"
            multiline
            rows={5}
            fullWidth
            value={formData.reason}
            onChange={e => onFormDataChange('reason', e.target.value)}
            placeholder="Enter reason for cancellation"
            disabled={loading || success}
            required
          />

          {/* Additional Notes (Optional) */}
          <TextField
            label="Additional Notes"
            multiline
            rows={4}
            fullWidth
            value={additionalNotes}
            onChange={e => setAdditionalNotes(e.target.value)}
            placeholder="Enter any additional notes (optional)"
            disabled={loading || success}
          />

          {/* Supporting Document (Required) */}
          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Supporting Document
              <Typography
                component="span"
                sx={{ color: 'error.main', ml: 0.5 }}
              >
                *
              </Typography>
            </Typography>
            <DropzoneS3Uploader
              onFinish={handleDocumentUploadFinished}
              upload={uploadOptions}
              s3Url={S3_URL}
              passChildrenProps={false}
              maxSize={1024 * 1024 * 5}
              style={{ width: '100%' }}
              disabled={(progress > 0 && progress < 100) || uploadedFile !== null}
            >
              <div
                style={{
                  border: '2px dashed #ccc',
                  borderRadius: '4px',
                  padding: '20px',
                  textAlign: 'center',
                  cursor: ((progress > 0 && progress < 100) || uploadedFile) ? 'not-allowed' : 'pointer',
                  backgroundColor: ((progress > 0 && progress < 100) || uploadedFile) ? '#f5f5f5' : '#fafafa',
                  opacity: ((progress > 0 && progress < 100) || uploadedFile) ? 0.6 : 1,
                  width: '100%',
                  boxSizing: 'border-box',
                  transition: 'opacity 0.3s ease'
                }}
              >
                {progress > 0 && progress < 100 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                    <CircularProgress size={40} sx={{ mb: 1 }} />
                    <Typography variant="body2">
                      Uploading... {progress}%
                    </Typography>
                  </Box>
                ) : uploadedFile ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                      ✓ {uploadedFile.filename}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Drag and drop your document here or click to select
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      (Max 5MB)
                    </Typography>
                  </>
                )}
              </div>
            </DropzoneS3Uploader>
            {progress > 0 && progress < 100 && (
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    width: '100%',
                    height: 4,
                    backgroundColor: '#e0e0e0',
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      backgroundColor: '#4caf50',
                      width: `${progress}%`,
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading || success || progress > 0}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            !formData.reason ||
            !formData.effective_date ||
            !uploadedFile ||
            loading ||
            success
          }
        >
          {loading ? <CircularProgress size={24} /> : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CancellationRequestDialog
